import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { User, LockKey, Envelope, Shield, ShieldCheck, DeviceMobile, QrCode, Trash, Monitor, PencilSimple, FloppyDisk } from "@phosphor-icons/react";

export default function ProfilePage() {
  useDocumentTitle("Profilo");
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  // Profile info editing
  const [infoEdit, setInfoEdit] = useState(false);
  const [infoForm, setInfoForm] = useState({ name: "", email: "" });
  const [savingInfo, setSavingInfo] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      setInfoForm({ name: user.name || "", email: user.email || "" });
    }
  }, [user]);

  const saveInfo = async () => {
    const changes = {};
    if ((infoForm.name || "").trim() && infoForm.name.trim() !== user?.name) changes.name = infoForm.name.trim();
    if ((infoForm.email || "").trim() && infoForm.email.trim().toLowerCase() !== user?.email) changes.email = infoForm.email.trim();
    if (Object.keys(changes).length === 0) {
      setInfoEdit(false);
      return;
    }
    setSavingInfo(true);
    try {
      const { data } = await api.patch("/auth/me", changes);
      updateUser({ name: data.name, email: data.email });
      toast.success("Dati aggiornati");
      setInfoEdit(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingInfo(false);
    }
  };

  // 2FA state
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpLoading, setTotpLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { qr_code, secret }
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });
  const [showDisable, setShowDisable] = useState(false);

  const loadTotp = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setTotpEnabled(data.enabled);
    } finally {
      setTotpLoading(false);
    }
  };

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const { data } = await api.get("/auth/trusted-devices");
      setDevices(data);
    } catch (e) {
      // silent
    } finally {
      setDevicesLoading(false);
    }
  };

  const revokeDevice = async (d) => {
    if (!window.confirm(`Revocare "${d.label}"? Al prossimo accesso da questo dispositivo verrà richiesto il codice 2FA.`)) return;
    try {
      await api.delete(`/auth/trusted-devices/${d.id}`);
      toast.success("Dispositivo revocato");
      loadDevices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const revokeAllDevices = async () => {
    if (!window.confirm("Revocare TUTTI i dispositivi fidati? Al prossimo login verrà richiesta la 2FA su tutti.")) return;
    try {
      await api.delete("/auth/trusted-devices");
      toast.success("Tutti i dispositivi revocati");
      loadDevices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { loadTotp(); loadDevices(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error("Nuova password minima 8 caratteri");
    if (form.next !== form.confirm) return toast.error("Le password non coincidono");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password aggiornata");
      setForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const startTotpSetup = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const verifyTotp = async () => {
    if (verifyCode.length !== 6) return toast.error("Inserisci il codice a 6 cifre");
    setVerifying(true);
    try {
      await api.post("/auth/2fa/verify", { totp_code: verifyCode });
      toast.success("2FA attivata con successo!");
      setSetupData(null);
      setVerifyCode("");
      setTotpEnabled(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setVerifying(false);
    }
  };

  const disableTotp = async () => {
    if (disableForm.code.length !== 6) return toast.error("Codice 2FA a 6 cifre");
    try {
      await api.post("/auth/2fa/disable", {
        password: disableForm.password,
        totp_code: disableForm.code,
      });
      toast.success("2FA disattivata");
      setTotpEnabled(false);
      setShowDisable(false);
      setDisableForm({ password: "", code: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// PROFILO</div>
        <h1 className="text-4xl font-display font-bold tracking-tight">Il tuo account</h1>
      </div>

      <div className="grid gap-6">
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <User size={20} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight text-lg">Informazioni</h2>
            </div>
            {!infoEdit && (
              <Button variant="outline" size="sm" onClick={() => setInfoEdit(true)} data-testid="edit-info-btn" className="rounded-full gap-1">
                <PencilSimple size={13} /> Modifica
              </Button>
            )}
          </div>
          {!infoEdit ? (
            <div className="space-y-3 text-sm">
              <Row icon={User} label="Nome" value={user?.name} />
              <Row icon={Envelope} label="Email" value={user?.email} />
              <Row icon={Shield} label="Ruolo" value={user?.role === "admin" ? "Amministratore" : "Tecnico"} />
            </div>
          ) : (
            <div className="space-y-4 max-w-md">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Nome</Label>
                <Input
                  value={infoForm.name}
                  onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })}
                  className="mt-1.5"
                  data-testid="info-name-input"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email</Label>
                <Input
                  type="email"
                  value={infoForm.email}
                  onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })}
                  className="mt-1.5"
                  data-testid="info-email-input"
                />
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">La nuova email sarà utilizzata anche per il login.</div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Ruolo</Label>
                <div className="mt-1.5 text-sm text-muted-foreground font-mono">{user?.role === "admin" ? "Amministratore" : "Tecnico"} <span className="opacity-60">(non modificabile da qui)</span></div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" onClick={() => { setInfoEdit(false); setInfoForm({ name: user?.name || "", email: user?.email || "" }); }}>Annulla</Button>
                <Button onClick={saveInfo} disabled={savingInfo} data-testid="save-info-btn" className="rounded-full gap-2">
                  <FloppyDisk size={14} weight="bold" /> {savingInfo ? "Salvo…" : "Salva"}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center gap-2 mb-5">
            <LockKey size={20} weight="duotone" className="text-primary" />
            <h2 className="font-display font-bold tracking-tight text-lg">Cambia password</h2>
          </div>
          <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Password attuale</Label>
              <Input type="password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className="mt-1.5" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Nuova password (min 8 caratteri)</Label>
              <Input type="password" required minLength={8} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className="mt-1.5" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Conferma nuova password</Label>
              <Input type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5" data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={saving} data-testid="change-password-btn" className="rounded-full gap-2">
              <LockKey size={16} weight="bold" /> {saving ? "Salvo..." : "Aggiorna password"}
            </Button>
          </form>
        </Card>

        {/* 2FA Section */}
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <DeviceMobile size={20} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight text-lg">Autenticazione a due fattori</h2>
            </div>
            {!totpLoading && (
              totpEnabled
                ? <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15 font-mono text-[10px] uppercase">Attiva</Badge>
                : <Badge className="bg-muted text-muted-foreground border border-border font-mono text-[10px] uppercase">Non attiva</Badge>
            )}
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Aggiungi un livello di sicurezza extra al login richiedendo un codice a 6 cifre dalla tua app di autenticazione (Google Authenticator, Authy, 1Password, ecc.).
          </p>

          {!totpEnabled && !setupData && (
            <Button onClick={startTotpSetup} data-testid="enable-2fa-btn" className="rounded-full gap-2">
              <ShieldCheck size={16} weight="bold" /> Attiva 2FA
            </Button>
          )}

          {!totpEnabled && setupData && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="bg-white p-3 border-2 border-border rounded-md">
                  <img src={setupData.qr_code} alt="QR 2FA" className="w-44 h-44" data-testid="totp-qr-image" />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Passo 1</div>
                    <div className="text-sm">Apri l'app di autenticazione (Google Authenticator) e scansiona il QR code.</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Codice manuale (se non puoi scansionare)</div>
                    <div className="font-mono text-xs bg-secondary/60 px-2 py-1.5 rounded break-all" data-testid="totp-secret">{setupData.secret}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Passo 2 — Verifica</div>
                    <div className="text-sm mb-3">Inserisci il codice a 6 cifre generato dall'app:</div>
                    <InputOTP maxLength={6} value={verifyCode} onChange={setVerifyCode} data-testid="totp-verify-input">
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                      </InputOTPGroup>
                    </InputOTP>
                    <div className="flex gap-2 mt-3">
                      <Button onClick={verifyTotp} disabled={verifying || verifyCode.length !== 6} data-testid="totp-verify-btn" className="rounded-full gap-2">
                        <ShieldCheck size={16} weight="bold" /> {verifying ? "Verifico..." : "Attiva 2FA"}
                      </Button>
                      <Button variant="ghost" onClick={() => { setSetupData(null); setVerifyCode(""); }} data-testid="totp-cancel-btn" className="rounded-full">Annulla</Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {totpEnabled && !showDisable && (
            <Button variant="outline" onClick={() => setShowDisable(true)} data-testid="disable-2fa-btn" className="rounded-full gap-2 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">
              Disattiva 2FA
            </Button>
          )}

          {totpEnabled && showDisable && (
            <div className="space-y-3 max-w-md border-l-2 border-destructive/40 pl-4">
              <div className="text-sm text-muted-foreground">
                Per disattivare la 2FA, conferma con password e codice attuale.
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Password</Label>
                <Input type="password" value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} className="mt-1.5" data-testid="disable-2fa-password" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Codice 2FA (6 cifre)</Label>
                <InputOTP maxLength={6} value={disableForm.code} onChange={(v) => setDisableForm({ ...disableForm, code: v })} data-testid="disable-2fa-code">
                  <InputOTPGroup className="mt-1.5">
                    {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={disableTotp} data-testid="disable-2fa-confirm" className="rounded-full">Disattiva</Button>
                <Button variant="ghost" onClick={() => { setShowDisable(false); setDisableForm({ password: "", code: "" }); }} className="rounded-full">Annulla</Button>
              </div>
            </div>
          )}
        </Card>

        {/* Trusted Devices (only meaningful if 2FA is on) */}
        <Card className="p-6 border-border/70 rounded-lg" data-testid="trusted-devices-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Monitor size={20} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight text-lg">Dispositivi fidati</h2>
            </div>
            {devices.length > 0 && (
              <Button variant="ghost" size="sm" onClick={revokeAllDevices} data-testid="revoke-all-devices-btn" className="text-destructive hover:text-destructive rounded-full gap-1">
                <Trash size={14} /> Revoca tutti
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Sui dispositivi in cui hai scelto "Fidati di questo dispositivo per 90 giorni" al login, il codice 2FA non verrà più richiesto fino alla scadenza.
          </p>
          {devicesLoading && <div className="text-sm text-muted-foreground">Caricamento…</div>}
          {!devicesLoading && devices.length === 0 && (
            <div className="text-sm text-muted-foreground/70 font-mono">— Nessun dispositivo fidato attivo —</div>
          )}
          {!devicesLoading && devices.length > 0 && (
            <div className="space-y-2">
              {devices.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 border border-border/60 rounded-md px-3 py-2.5" data-testid={`device-row-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-md bg-secondary grid place-items-center">
                      <Monitor size={16} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {d.label}
                        {d.is_current && <Badge className="bg-primary/15 text-primary border border-primary/30 hover:bg-primary/15 font-mono text-[9px] uppercase">Questo</Badge>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        Aggiunto: {d.created_at ? new Date(d.created_at).toLocaleString("it-IT") : "—"} · Scade: {d.expires_at ? new Date(d.expires_at).toLocaleDateString("it-IT") : "—"}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => revokeDevice(d)} className="text-destructive hover:text-destructive rounded-full" data-testid={`revoke-device-${d.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 last:border-0 py-2">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={14} /> <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
